import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import type { DisciplineSlug } from "@jumpbean/shared";

export type CardVariant = "default" | "elevated" | "interactive" | "discipline";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Visual style variant */
  variant?: CardVariant;
  /** Discipline theming when variant is "discipline" */
  discipline?: DisciplineSlug;
  /** Card content */
  children: ReactNode;
  /** Optional header content */
  header?: ReactNode;
  /** Optional footer content */
  footer?: ReactNode;
  /** Whether the card shows a locked state */
  isLocked?: boolean;
  /** Whether the card is in a completed state */
  isCompleted?: boolean;
}

/**
 * Card component for containing content in a rounded, shadowed container.
 * Features soft shadows and rounded corners for a friendly, storybook feel.
 *
 * @example
 * // Basic card
 * <Card>
 *   <p>Card content here</p>
 * </Card>
 *
 * @example
 * // Interactive discipline card
 * <Card variant="discipline" discipline="math" isCompleted>
 *   <h3>Level 1</h3>
 *   <p>Counting Stars</p>
 * </Card>
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = "default",
      discipline,
      children,
      header,
      footer,
      isLocked = false,
      isCompleted = false,
      className = "",
      ...props
    },
    ref
  ) => {
    // Base styles for all cards
    const baseStyles = `
      relative
      overflow-hidden
      rounded-2xl
      bg-cloud
      transition-all duration-300
    `;

    // Variant styles
    const variantStyles = {
      default: `
        border-[3px] border-neutral-200
        shadow-[0_4px_16px_rgba(0,0,0,0.08),0_1px_4px_rgba(0,0,0,0.04)]
      `,
      elevated: `
        border-[3px] border-neutral-200
        shadow-[0_8px_24px_rgba(0,0,0,0.12),0_4px_8px_rgba(0,0,0,0.06)]
      `,
      interactive: `
        border-[3px] border-neutral-200
        shadow-[0_4px_16px_rgba(0,0,0,0.08),0_1px_4px_rgba(0,0,0,0.04)]
        cursor-pointer
        hover:border-magic-300
        hover:shadow-[0_8px_24px_rgba(0,0,0,0.12),0_4px_8px_rgba(0,0,0,0.06)]
        hover:-translate-y-1
        active:translate-y-0
        active:shadow-[0_2px_8px_rgba(0,0,0,0.08)]
      `,
      discipline: "",
    };

    // Discipline-specific border and accent colors
    const disciplineStyles: Record<DisciplineSlug, string> = {
      math: `
        border-[3px] border-luna-light
        shadow-[0_4px_16px_rgba(232,160,255,0.2),0_1px_4px_rgba(0,0,0,0.04)]
        hover:border-luna
        hover:shadow-[0_8px_24px_rgba(232,160,255,0.3),0_4px_8px_rgba(0,0,0,0.06)]
      `,
      spelling: `
        border-[3px] border-petal-light
        shadow-[0_4px_16px_rgba(144,238,144,0.2),0_1px_4px_rgba(0,0,0,0.04)]
        hover:border-petal
        hover:shadow-[0_8px_24px_rgba(144,238,144,0.3),0_4px_8px_rgba(0,0,0,0.06)]
      `,
      patterns: `
        border-[3px] border-transparent
        bg-gradient-to-br from-white to-white
        shadow-[0_4px_16px_rgba(255,107,107,0.15),0_1px_4px_rgba(0,0,0,0.04)]
        hover:shadow-[0_8px_24px_rgba(255,107,107,0.25),0_4px_8px_rgba(0,0,0,0.06)]
        before:content-[''] before:absolute before:inset-0 before:-z-10
        before:bg-gradient-to-r before:from-prism-red before:via-prism-yellow before:to-prism-blue
        before:rounded-2xl before:p-[3px]
      `,
      typing: `
        border-[3px] border-whiskers-light
        shadow-[0_4px_16px_rgba(74,144,217,0.2),0_1px_4px_rgba(0,0,0,0.04)]
        hover:border-whiskers
        hover:shadow-[0_8px_24px_rgba(74,144,217,0.3),0_4px_8px_rgba(0,0,0,0.06)]
      `,
      programming: `
        border-[3px] border-pixel-light
        shadow-[0_4px_16px_rgba(255,140,66,0.2),0_1px_4px_rgba(0,0,0,0.04)]
        hover:border-pixel
        hover:shadow-[0_8px_24px_rgba(255,140,66,0.3),0_4px_8px_rgba(0,0,0,0.06)]
      `,
    };

    // State modifiers
    const lockedStyles = isLocked
      ? "opacity-60 grayscale-[40%] cursor-not-allowed"
      : "";

    const completedStyles = isCompleted
      ? "border-meadow-400 bg-gradient-to-br from-cloud to-meadow-50"
      : "";

    const getVariantStyle = () => {
      if (variant === "discipline" && discipline) {
        return `${disciplineStyles[discipline]} cursor-pointer hover:-translate-y-1 active:translate-y-0`;
      }
      return variantStyles[variant];
    };

    return (
      <div
        ref={ref}
        className={`
          ${baseStyles}
          ${getVariantStyle()}
          ${lockedStyles}
          ${completedStyles}
          ${className}
        `}
        {...props}
      >
        {/* Lock overlay for locked cards */}
        {isLocked && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-neutral-100/50 rounded-2xl">
            <div className="w-12 h-12 rounded-full bg-neutral-300 flex items-center justify-center">
              <LockIcon className="w-6 h-6 text-neutral-500" />
            </div>
          </div>
        )}

        {/* Completion sparkle decoration */}
        {isCompleted && !isLocked && (
          <div className="absolute top-2 right-2 z-10">
            <SparkleIcon className="w-6 h-6 text-sunshine-500 animate-sparkle" />
          </div>
        )}

        {/* Header section */}
        {header && (
          <div className="px-5 pt-5 pb-3 border-b border-neutral-100">
            {header}
          </div>
        )}

        {/* Main content */}
        <div className="p-5">{children}</div>

        {/* Footer section */}
        {footer && (
          <div className="px-5 pb-5 pt-3 border-t border-neutral-100">
            {footer}
          </div>
        )}
      </div>
    );
  }
);

Card.displayName = "Card";

/** Simple lock icon */
function LockIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

/** Sparkle decoration icon */
function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
    </svg>
  );
}

export default Card;
